import React, { useState, useEffect, useRef } from "react";
import "./App.css";
import * as pdfjsLib from "pdfjs-dist";

// Use the bundled worker
pdfjsLib.GlobalWorkerOptions.workerSrc = `${process.env.PUBLIC_URL}/pdf.worker.min.mjs`;

/* ================================================================
   SKILL ONTOLOGY  (ported from C++ FileManager.cpp)
   key = canonical skill name, value = synonyms to detect in text
   ================================================================ */
const SKILL_ONTOLOGY = {
  "html":                  ["html"],
  "css":                   ["css"],
  "javascript":            ["javascript", "js"],
  "react":                 ["react", "reactjs"],
  "angular":               ["angular"],
  "vue":                   ["vue"],
  "bootstrap":             ["bootstrap"],
  "tailwind":              ["tailwind"],
  "c++":                   ["c++", "cpp"],
  "java":                  ["java"],
  "python":                ["python", "tensorflow"],
  "node":                  ["node", "nodejs"],
  "express":               ["express"],
  "rest api":              ["rest api", "restful"],
  "oop":                   ["oop", "object oriented"],
  "data structures":       ["data structures", "dsa", "data structure"],
  "algorithm":             ["algorithm", "algorithms"],
  "system design":         ["system design"],
  "sql":                   ["sql"],
  "mysql":                 ["mysql"],
  "postgresql":            ["postgresql"],
  "mongodb":               ["mongodb"],
  "database":              ["database"],
  "linux":                 ["linux"],
  "docker":                ["docker"],
  "kubernetes":            ["kubernetes", "k8s"],
  "ci/cd":                 ["ci/cd", "continuous integration", "continuous deployment"],
  "jenkins":               ["jenkins"],
  "terraform":             ["terraform"],
  "aws":                   ["aws", "amazon web services"],
  "cloud computing":       ["cloud computing"],
  "bash":                  ["bash"],
  "git":                   ["git"],
  "github":                ["github"],
  "machine learning":      ["machine learning", "ml"],
  "deep learning":         ["deep learning"],
  "artificial intelligence": ["artificial intelligence", "ai"],
  "data science":          ["data science"],
  "etl":                   ["etl"],
  "data pipeline":         ["data pipeline"],
  "spark":                 ["spark"],
  "hadoop":                ["hadoop"],
  "airflow":               ["airflow"],
  "big data":              ["big data"],
  "data warehouse":        ["data warehouse"],
  "android":               ["android"],
  "kotlin":                ["kotlin"],
  "firebase":              ["firebase"],
  "xml":                   ["xml"],
  "flutter":               ["flutter"],
  "react native":          ["react native"],
  "network security":      ["network security"],
  "cyber security":        ["cyber security", "cybersecurity"],
  "cryptography":          ["cryptography"],
  "penetration testing":   ["penetration testing", "pentesting"],
  "ethical hacking":       ["ethical hacking"],
  "firewalls":             ["firewalls"],
  "wireshark":             ["wireshark"],
  "vulnerability assessment": ["vulnerability assessment"],
  "incident response":     ["incident response"],
  "coding problem":        ["coding problem", "competitive programming"],
  "problem solving":       ["problem solving"],
  "api":                   ["api"],
  "ui":                    ["ui"],
  "web development":       ["web development"],
  "monitoring":            ["monitoring"],
  "material design":       ["material design"],
  "mvvm":                  ["mvvm"],
  "gradle":                ["gradle"],
  "dsa":                   ["dsa"],
  "tensorflow":            ["tensorflow"],
  "pytorch":               ["pytorch"],
};

/* ================================================================
   JOB PROFILES  (ported from job_profiles/*.txt)
   mandatory = skills that auto-reject if missing
   skills = { skillName: weight }
   ================================================================ */
const JOB_PROFILES = [
  {
    title: "Frontend Developer",
    mandatory: ["html", "javascript"],
    skills: { html:35, css:35, javascript:40, react:30, bootstrap:20, ui:20, "web development":25, git:10 },
  },
  {
    title: "Backend Developer",
    mandatory: ["c++", "data structures"],
    skills: { dsa:30, "c++":40, "data structures":30, algorithm:30, oop:25, sql:20, "system design":20, java:20, python:15, git:10, linux:10, "coding problem":30 },
  },
  {
    title: "Full Stack Developer",
    mandatory: ["javascript", "react"],
    skills: { javascript:40, react:35, node:35, express:30, mongodb:30, "rest api":30, html:25, css:25, sql:20, git:15 },
  },
  {
    title: "ML Engineer",
    mandatory: ["python", "machine learning"],
    skills: { python:40, "machine learning":40, "artificial intelligence":30, tensorflow:30, pytorch:30, "c++":10 },
  },
  {
    title: "DevOps Engineer",
    mandatory: ["linux", "docker"],
    skills: { linux:40, docker:40, kubernetes:35, "ci/cd":35, git:25, aws:30, terraform:25, jenkins:30, bash:25, "cloud computing":30, monitoring:20 },
  },
  {
    title: "Android Developer",
    mandatory: ["java", "android"],
    skills: { java:40, kotlin:35, android:40, xml:25, firebase:25, "rest api":20, sql:20, git:15, "material design":20, mvvm:25, gradle:15 },
  },
  {
    title: "Data Engineer",
    mandatory: ["python", "sql"],
    skills: { python:40, sql:40, etl:35, "data pipeline":35, spark:30, hadoop:30, airflow:25, "data warehouse":30, aws:25, linux:20, "big data":35 },
  },
  {
    title: "Cyber Security Analyst",
    mandatory: ["network security", "linux"],
    skills: { "network security":40, linux:35, cryptography:30, "penetration testing":35, firewalls:30, wireshark:25, "ethical hacking":35, "vulnerability assessment":30, "cyber security":40, python:20, "incident response":30 },
  },
];

/* ================================================================
   HELPERS
   ================================================================ */
function getFitCategory(pct) {
  if (pct >= 80) return "Excellent Fit";
  if (pct >= 70) return "Strong Fit";
  if (pct >= 30) return "Moderate Fit";
  return "Weak Fit";
}

function extractNameFromText(lines) {
  // Check first 2 non-empty lines for a name (2-3 alpha words)
  let checked = 0;
  for (const raw of lines) {
    const line = raw.trim();
    if (!line) continue;
    if (++checked > 3) break;
    // strip digits, phone chars, email part
    let cleaned = line.replace(/[\d+\-()]/g, " ");
    const atIdx = cleaned.indexOf("@");
    if (atIdx !== -1) cleaned = cleaned.substring(0, atIdx);
    // strip common social words
    ["linkedin","github","leetcode","portfolio","instagram","behance"].forEach(w => {
      const idx = cleaned.toLowerCase().indexOf(w);
      if (idx !== -1) cleaned = cleaned.substring(0, idx);
    });
    const words = cleaned.trim().split(/\s+/).filter(w => /^[A-Za-z]+$/.test(w)).slice(0, 3);
    if (words.length >= 2) return words.join(" ");
  }
  return "Name Not Detected";
}

function extractEmail(text) {
  const m = text.match(/[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}/);
  return m ? m[0] : "";
}

function detectSkills(fullText) {
  const lower = fullText.toLowerCase();
  const found = new Set();
  for (const [canonical, synonyms] of Object.entries(SKILL_ONTOLOGY)) {
    for (const syn of synonyms) {
      if (lower.includes(syn)) { found.add(canonical); break; }
    }
  }
  return [...found];
}

function detectSections(lines) {
  let certs = [], education = [], projects = [];
  let section = null;
  for (const raw of lines) {
    const line = raw.trim();
    if (!line) continue;
    const lower = line.toLowerCase();
    if (lower.includes("education"))       { section = "edu"; continue; }
    if (lower.includes("project"))         { section = "proj"; continue; }
    if (lower.includes("certification"))   { section = "cert"; continue; }
    if (lower.includes("experience") || lower.includes("skills") || lower.includes("summary")) {
      section = null; continue;
    }
    if (section === "edu")  education.push(line);
    if (section === "proj") projects.push(line);
    if (section === "cert") certs.push(line);
  }
  return { education, projects, certs };
}

/* ================================================================
   PDF → text extraction using pdf.js
   ================================================================ */
async function extractTextFromPDF(file) {
  const arrayBuffer = await file.arrayBuffer();
  const pdf = await pdfjsLib.getDocument({ data: arrayBuffer }).promise;
  let fullText = "";
  const lines = [];
  for (let p = 1; p <= pdf.numPages; p++) {
    const page = await pdf.getPage(p);
    const content = await page.getTextContent();
    const pageText = content.items.map(i => i.str).join(" ");
    fullText += pageText + "\n";
    // approximate line breaks by grouping items with same y
    const byY = {};
    content.items.forEach(item => {
      const y = Math.round(item.transform[5]);
      if (!byY[y]) byY[y] = [];
      byY[y].push(item.str);
    });
    Object.keys(byY).sort((a,b) => b-a).forEach(y => lines.push(byY[y].join(" ")));
  }
  return { fullText, lines };
}

/* ================================================================
   MATCHING ENGINE  (ported from C++ JobMatcher.cpp)
   ================================================================ */
function matchJobs(candidateSkills, certCount, hasEducation, projects) {
  const results = JOB_PROFILES.map(job => {
    // 1. Mandatory skill check
    for (const req of job.mandatory) {
      if (!candidateSkills.includes(req)) {
        return { role: job.title, percentage: 0, fitCategory: "Rejected", matched_skills: [], missing_skills: [] };
      }
    }

    let rawScore = 0;
    let maxScore = 0;
    const matchedSkills = [];
    const missingSkillsWeighted = [];

    // 2. Weighted skill scoring
    for (const [skill, weight] of Object.entries(job.skills)) {
      maxScore += weight;
      if (candidateSkills.includes(skill)) {
        rawScore += weight;
        matchedSkills.push(skill);
      } else {
        missingSkillsWeighted.push([skill, weight]);
      }
    }
    missingSkillsWeighted.sort((a,b) => b[1]-a[1]);
    const missing_skills = missingSkillsWeighted.map(([s]) => s);

    // 3. Certification bonus (3 pts each, max 5 certs = 15 pts)
    const certBonus = Math.min(certCount, 5) * 3;
    rawScore += certBonus;
    maxScore += 15;

    // 4. Education bonus
    if (hasEducation) rawScore += 5;
    maxScore += 5;

    // 5. Project relevance bonus (4 pts per project mentioning a required skill)
    const jobSkillNames = Object.keys(job.skills);
    for (const proj of projects) {
      const lowerProj = proj.toLowerCase();
      if (jobSkillNames.some(s => lowerProj.includes(s))) {
        rawScore += 4;
      }
    }
    maxScore += jobSkillNames.length * 4;

    // 6. Final percentage
    const pct = maxScore > 0 ? Math.round((rawScore * 100.0 / maxScore) * 100) / 100 : 0;
    return { role: job.title, percentage: pct, fitCategory: getFitCategory(pct), matched_skills: matchedSkills, missing_skills };
  });

  // Sort descending
  results.sort((a, b) => b.percentage - a.percentage);
  const top3 = results.slice(0, 3);
  const best = top3[0];

  return {
    success: true,
    data: {
      request_id: Date.now().toString(),
      name: "",        // filled in by caller
      email: "",       // filled in by caller
      best_role: best.role,
      percentage: best.percentage.toFixed(2),
      fit_category: best.fitCategory,
      comparison: Object.fromEntries(top3.map(r => [r.role, r.percentage.toFixed(2)])),
      top_roles: top3,
      matched_skills: best.matched_skills,
      missing_skills: best.missing_skills,
    }
  };
}

/* ================= COMPARISON BAR ================= */
function ComparisonBar({ role, score, index, onClick, isSelected }) {
  const [width, setWidth] = useState(0);

  useEffect(() => {
    const t = setTimeout(() => setWidth(score), 150 + index * 130);
    return () => clearTimeout(t);
  }, [score, index]);

  return (
    <div
      className={`comparison-item ${isSelected ? "selected" : ""}`}
      onClick={onClick}
      role="button"
      tabIndex={0}
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " ") {
          onClick?.();
        }
      }}
    >
      <div className="comparison-meta">
        <span className="comparison-role">{role}</span>
        <span className="comparison-pct">{score}%</span>
      </div>
      <div className="bar-track">
        <div className="bar-fill" style={{ width: `${width}%` }} />
      </div>
    </div>
  );
}

/* ================= BEST MATCH CARD ================= */
function BestMatchCard({ role, percent, fitCategory }) {
  const [animPct, setAnimPct] = useState(0);

  useEffect(() => {
    setAnimPct(0);
    const end = parseFloat(percent);
    const inc = end / 100;
    let cur = 0;

    const t = setInterval(() => {
      cur += inc;
      if (cur >= end) { cur = end; clearInterval(t); }
      setAnimPct(cur.toFixed(2));
    }, 10);

    return () => clearInterval(t);
  }, [percent]);

  return (
    <div className="best-match-card fade-in">
      <div>
        <div className="bm-eyebrow">Best Match</div>
        <div className="bm-role">{role}</div>
      </div>
      <div className="bm-bottom">
        <div className="bm-percent">{animPct}<sup>%</sup></div>
        <div className="fit-badge">{fitCategory}</div>
      </div>
    </div>
  );
}

/* ================= PANEL FOOTER ================= */
function PanelFooter() {
  return (
    <div className="panel-footer">
      <p>
        &copy; 2026 Resume Matcher |{" "}
        <a href="https://digitalheroesco.com" target="_blank" rel="noopener noreferrer">
          Built for Digital Heroes
        </a>
      </p>
      <p>
        Developed by Shubhi Gupta &middot;{" "}
        <a href="mailto:shubhigupta775@gmail.com">shubhigupta775@gmail.com</a>
      </p>
    </div>
  );
}


/* ================= ANIMATED LOADING STATE ================= */
const LOADING_STEPS = [
  "Reading your resume…",
  "Extracting skills…",
  "Matching against roles…",
  "Computing fit scores…",
  "Almost done…",
];

function LoadingState() {
  const [step, setStep] = useState(0);
  const [dots, setDots] = useState("");

  useEffect(() => {
    const stepTimer = setInterval(() => {
      setStep(s => (s + 1) % LOADING_STEPS.length);
    }, 2000);

    const dotTimer = setInterval(() => {
      setDots(d => d.length >= 3 ? "" : d + ".");
    }, 400);

    return () => { clearInterval(stepTimer); clearInterval(dotTimer); };
  }, []);

  return (
    <div className="result-panel panel-card">
      <div className="result-topbar">
        <div className="topbar-dot" />
        <span className="topbar-label">Analysis Results</span>
        <span className="topbar-slash">/</span>
        <span className="topbar-sub">Resume Matcher</span>
      </div>

      <div className="loading-state">
        {/* Animated rings */}
        <div className="loading-rings">
          <div className="ring ring-1" />
          <div className="ring ring-2" />
          <div className="ring ring-3" />
          <div className="loading-icon">{"\u26A1"}</div>
        </div>

        {/* Animated text */}
        <div className="loading-title">Analyzing{dots}</div>
        <div className="loading-step fade-step" key={step}>{LOADING_STEPS[step]}</div>

        {/* Progress bar */}
        <div className="loading-bar-track">
          <div className="loading-bar-fill" />
        </div>
      </div>
      <PanelFooter />
    </div>
  );
}

/* ================= AI FEEDBACK CARD ================= */
const GEMINI_API_KEY = process.env.REACT_APP_GEMINI_API_KEY;
const GEMINI_MODEL = process.env.REACT_APP_GEMINI_MODEL || "gemini-2.5-flash";

function AIFeedbackCard({ requestId, role, matchedSkills, missingSkills }) {
  const [feedback, setFeedback] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [isExpanded, setIsExpanded] = useState(false);

  const fetchFeedback = async () => {
    if (!role) {
      setError("Role details are still loading. Please try again.");
      return;
    }
    if (!GEMINI_API_KEY) {
      setError("Gemini API key not configured. Please set REACT_APP_GEMINI_API_KEY.");
      return;
    }

    setLoading(true);
    setError(null);
    try {
      const prompt = `You are an expert recruiter reviewing a candidate's resume for the role of "${role}".

Matched skills (skills they already have): ${(matchedSkills || []).join(", ") || "none listed"}
Missing skills (skills they need to acquire): ${(missingSkills || []).join(", ") || "none listed"}

Provide a concise, actionable recruiter analysis. Return ONLY valid JSON with this exact structure (no markdown, no code fences):
{
  "strengths": ["strength 1", "strength 2", "strength 3"],
  "weaknesses": ["weakness 1", "weakness 2", "weakness 3"],
  "improvement_tips": ["tip 1", "tip 2", "tip 3"]
}`;

      const response = await fetch(
        `https://generativelanguage.googleapis.com/v1beta/models/${GEMINI_MODEL}:generateContent?key=${GEMINI_API_KEY}`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            contents: [{ parts: [{ text: prompt }] }],
            generationConfig: { temperature: 0.7, maxOutputTokens: 1024 }
          })
        }
      );

      if (!response.ok) {
        const errText = await response.text();
        throw new Error(`Gemini API error (${response.status}): ${errText.substring(0, 200)}`);
      }

      const geminiData = await response.json();
      const rawText = geminiData?.candidates?.[0]?.content?.parts?.[0]?.text || "";

      // Strip markdown code fences if present
      const cleaned = rawText.replace(/```json\s*/gi, "").replace(/```/g, "").trim();
      let data;
      try { data = JSON.parse(cleaned); } catch(e) { throw new Error("Could not parse AI response. Raw: " + cleaned.substring(0, 150)); }

      setFeedback(data);
    } catch (err) {
      setError(err.message || "Unable to reach AI service.");
    }
    setLoading(false);
  };

  useEffect(() => {
    // Reset feedback when resume changes (new upload)
    setFeedback(null);
    setError(null);
    setIsExpanded(false);
  }, [requestId]);

  const glassStyle = {
    background: 'rgba(255, 255, 255, 0.4)',
    backdropFilter: 'blur(12px)',
    WebkitBackdropFilter: 'blur(12px)',
    border: '1px solid rgba(255, 255, 255, 0.8)',
    borderRadius: '12px',
    padding: '16px',
    boxShadow: '0 4px 16px rgba(0, 0, 0, 0.03)'
  };

  const headerStyle = {
    color: 'var(--text-1)',
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    marginBottom: '10px',
    fontSize: '15px',
    fontWeight: '600',
    fontFamily: "'Playfair Display', serif"
  };

  const listStyle = {
    margin: 0,
    paddingLeft: '24px',
    color: 'var(--text-2)',
    fontSize: '13.5px',
    lineHeight: '1.6',
    fontWeight: '400'
  };

  return (
    <div className="stat-card fade-in ai-feedback-card" style={{ marginTop: '16px', borderTop: '4px solid #B8D8F8' }}>
      <div className="stat-card-label" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
        <span>✨ AI Recruiter Analysis</span>
        {feedback && (
          <button 
            onClick={() => setIsExpanded(!isExpanded)}
            style={{
              background: 'none', border: 'none', cursor: 'pointer',
              color: 'var(--sky-deep)', fontSize: '12px', fontWeight: '600',
              padding: '4px 8px', borderRadius: '4px', display: 'flex', alignItems: 'center', gap: '4px'
            }}
          >
            {isExpanded ? 'Hide Details ▲' : 'View Full Report ▼'}
          </button>
        )}
      </div>

      {!feedback && !loading && !error && (
        <div style={{ ...glassStyle, textAlign: 'center', padding: '20px' }}>
          <p style={{ color: 'var(--text-3)', fontSize: '13px', marginBottom: '12px' }}>
            Get a personalized AI recruiter review of your resume for the <strong style={{ color: 'var(--sky-deep)' }}>{role}</strong> role.
          </p>
          <button className="btn-ai" onClick={fetchFeedback} disabled={!requestId || !role} style={{ margin: '0 auto' }}>
            ✨ Generate AI Analysis
          </button>
        </div>
      )}

      {loading && (
        <div style={{ ...glassStyle, textAlign: 'center', padding: '24px' }}>
          <div className="btn-spinner" style={{ margin: '0 auto', width: '24px', height: '24px', borderColor: 'rgba(30,111,171,0.3)', borderTopColor: 'var(--sky-deep)' }} />
          <p style={{ color: 'var(--sky-deep)', fontSize: '13px', fontWeight: '500', marginTop: '10px' }}>Generating Insights...</p>
        </div>
      )}

      {error && (
        <div style={{ ...glassStyle, textAlign: 'center' }}>
          <p className="error-msg">{error}</p>
          <button className="btn-ai" onClick={() => fetchFeedback()} disabled={!requestId || !role} style={{ marginTop: '12px', padding: '8px 16px', fontSize: '13px' }}>
            🔄 Retry Analysis
          </button>
        </div>
      )}

      {feedback && !isExpanded && (
        <div style={{ ...glassStyle, cursor: 'pointer' }} onClick={() => setIsExpanded(true)}>
          <div style={{ display: 'flex', alignItems: 'flex-start', gap: '12px' }}>
            <span style={{ fontSize: '18px', flexShrink: 0 }}>💡</span>
            <div>
              <h4 style={{ margin: '0 0 4px 0', fontSize: '14px', color: 'var(--text-1)' }}>Key Strength</h4>
              <p style={{ margin: 0, fontSize: '13px', color: 'var(--text-2)', lineHeight: '1.5' }}>
                {feedback.strengths?.[0] || "Strong match for this role based on core skills."}
              </p>
              <p style={{ margin: '8px 0 0 0', fontSize: '12px', color: 'var(--sky-deep)', fontWeight: '500' }}>
                Click to view full analysis →
              </p>
            </div>
          </div>
        </div>
      )}

      {feedback && isExpanded && (
        <div className="ai-feedback-content" style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
          
          <div className="feedback-section fade-in" style={glassStyle}>
            <h4 style={headerStyle}>
              <span style={{ opacity: 0.8 }}>🚀</span> Strengths
            </h4>
            <ul style={listStyle}>
              {feedback.strengths?.map((s, i) => <li key={i} style={{marginBottom: '6px'}}>{s}</li>)}
            </ul>
          </div>

          <div className="feedback-section fade-in" style={glassStyle}>
            <h4 style={headerStyle}>
              <span style={{ opacity: 0.8 }}>🎯</span> Areas to Improve
            </h4>
            <ul style={listStyle}>
              {feedback.weaknesses?.map((w, i) => <li key={i} style={{marginBottom: '6px'}}>{w}</li>)}
            </ul>
          </div>

          <div className="feedback-section fade-in" style={glassStyle}>
            <h4 style={headerStyle}>
              <span style={{ opacity: 0.8 }}>💡</span> Actionable Tips
            </h4>
            <ul style={listStyle}>
              {feedback.improvement_tips?.map((t, i) => <li key={i} style={{marginBottom: '6px'}}>{t}</li>)}
            </ul>
          </div>

        </div>
      )}
    </div>
  );
}

/* ================= RESULT PANEL ================= */
function ResultPanel({ result, loading }) {
  const [selectedRole, setSelectedRole] = useState("");

  useEffect(() => {
    if (!result?.success) {
      setSelectedRole("");
      return;
    }

    const { best_role, top_roles, comparison } = result.data;
    const firstRole = Array.isArray(top_roles)
      ? top_roles[0]?.role
      : Object.keys(comparison || {})[0];
    setSelectedRole(best_role || firstRole || "");
  }, [result]);

  const EmptyShell = ({ icon, title, sub }) => (
    <div className="result-panel panel-card">
      <div className="result-topbar">
        <div className="topbar-dot" />
        <span className="topbar-label">Analysis Results</span>
        <span className="topbar-slash">/</span>
        <span className="topbar-sub">Resume Matcher</span>
      </div>
      <div className="empty-state">
        <div className="empty-illustration">{icon}</div>
        <div className="empty-title">{title}</div>
        <div className="empty-sub">{sub}</div>
      </div>
      <PanelFooter />
    </div>
  );

  if (loading) return <LoadingState />;

  if (!result)
    return <EmptyShell icon={"\uD83D\uDCCB"} title="No analysis yet" sub="Upload your resume and click Analyze." />;

  if (!result.success)
    return <EmptyShell icon={"\u26A0\uFE0F"} title="Something went wrong" sub={result.message} />;

  const { request_id, name, email, best_role, percentage, fit_category, comparison, matched_skills, missing_skills } = result.data;

  const topRoles = Array.isArray(result.data.top_roles)
    ? result.data.top_roles
    : Object.entries(comparison || {}).map(([role, score]) => ({
        role,
        percentage: score,
        matched_skills: matched_skills || [],
        missing_skills: missing_skills || [],
      }));


  const selectedRoleData =
    topRoles.find((item) => item.role === selectedRole) || {
      matched_skills: matched_skills || [],
      missing_skills: missing_skills || [],
    };
  const feedbackRole = selectedRole || best_role || topRoles[0]?.role || "";

  return (
    <div className="result-panel panel-card">
      <div className="result-topbar">
        <div className="topbar-dot" />
        <span className="topbar-label">Analysis Results</span>
        <span className="topbar-slash">/</span>
        <span className="topbar-sub">Resume Matcher</span>
      </div>

      <div className="result-content-scroll" style={{ overflowY: 'auto', flex: 1, paddingBottom: '20px' }}>
        <div className="name-block">
          <span className="display-name">{name}</span>
          <span className="display-email">{email}</span>
        </div>

        <div className="top-row" style={{ height: 'auto', minHeight: '180px', paddingBottom: '16px' }}>
          <BestMatchCard role={best_role} percent={percentage} fitCategory={fit_category} />
          <div className="stat-card fade-in">
            <div className="stat-card-label">Role Comparison</div>
            <div className="comparison-list">
              {Object.entries(comparison || {}).map(([role, score], i) => (
                <ComparisonBar
                  key={role}
                  role={role}
                  score={parseFloat(score)}
                  index={i}
                  onClick={() => setSelectedRole(role)}
                  isSelected={selectedRole === role}
                />
              ))}
            </div>
          </div>
        </div>

        <div className="bottom-row" style={{ height: 'auto', overflow: 'visible', paddingBottom: '0' }}>
          <div className="stat-card fade-in">
            <div className="stat-card-label">Matched Skills</div>
            {selectedRoleData?.matched_skills?.length > 0 ? (
              <div className="skills-wrap">
                {selectedRoleData.matched_skills.map((s, i) => (
                  <span key={i} className="skill-tag matched" style={{ animationDelay: `${i * 0.04}s` }}>
                    {"\u2713"} {s}
                  </span>
                ))}
              </div>
            ) : <p className="no-skills">No matched skills found</p>}
          </div>

          <div className="stat-card fade-in">
            <div className="stat-card-label">Skills to Acquire</div>
            {selectedRoleData?.missing_skills?.length > 0 ? (
              <div className="skills-wrap">
                {selectedRoleData.missing_skills.map((s, i) => (
                  <span key={i} className="skill-tag missing" style={{ animationDelay: `${i * 0.04}s` }}>
                    + {s}
                  </span>
                ))}
              </div>
            ) : <p className="no-skills" style={{ color: "#065f46" }}>No missing skills.</p>}
          </div>
        </div>

        {best_role && (
          <div style={{ padding: '0 16px' }}>
            <AIFeedbackCard
              requestId={request_id}
              role={feedbackRole}
              matchedSkills={selectedRoleData?.matched_skills || matched_skills || []}
              missingSkills={selectedRoleData?.missing_skills || missing_skills || []}
            />
          </div>
        )}
      </div>
      <PanelFooter />
    </div>
  );
}

/* ================= UPLOAD PANEL ================= */
function UploadPanel({ onResult, onLoading, loading }) {
  const [file, setFile] = useState(null);
  const [dragActive, setDragActive] = useState(false);
  const [error, setError] = useState(null);
  const inputRef = useRef();

  const handleUpload = async () => {
    if (!file) { setError("Please select a file first."); return; }
    setError(null);
    onLoading(true);
    onResult(null);

    try {
      // 1. Extract text from PDF in the browser
      const { fullText, lines } = await extractTextFromPDF(file);

      // 2. Parse name & email
      const name  = extractNameFromText(lines);
      const email = extractEmail(fullText);

      // 3. Detect skills from full text
      const candidateSkills = detectSkills(fullText);

      // 4. Detect section data for bonuses
      const { education, projects, certs } = detectSections(lines);

      // 5. Run job matching algorithm
      const result = matchJobs(candidateSkills, certs.length, education.length > 0, projects);
      result.data.name  = name;
      result.data.email = email;

      onResult(result);
    } catch (err) {
      console.error(err);
      setError("Failed to process PDF: " + err.message);
      onResult(null);
    }

    onLoading(false);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setDragActive(false);
    const f = e.dataTransfer.files[0];
    if (f) setFile(f);
  };

  return (
    <div className="upload-panel panel-card">
      <div className="brand">
        <div className="brand-icon">{"\u2726"}</div>
        <div className="brand-name">Resume <span>Matcher</span></div>
      </div>

      <h2 className="upload-heading">Find your <span>perfect</span> role match</h2>

      <p className="upload-subtext">
        Upload your resume and instantly see how well you match top roles,
        what skills you have, and what to learn next.
      </p>

      <div
        className={`drop-zone ${dragActive ? "active" : ""} ${file ? "has-file" : ""}`}
        onDragOver={(e) => { e.preventDefault(); setDragActive(true); }}
        onDragLeave={() => setDragActive(false)}
        onDrop={handleDrop}
        onClick={() => inputRef.current.click()}
      >
        <input
          ref={inputRef}
          type="file"
          accept="application/pdf"
          hidden
          onChange={(e) => { if (e.target.files[0]) setFile(e.target.files[0]); }}
        />
        <span className="drop-icon">{file ? "\uD83D\uDCC4" : "\u2601\uFE0F"}</span>
        {file ? (
          <div className="drop-file-info">
            <span className="drop-filename">{file.name}</span>
            <span className="drop-sub">Click to change file</span>
          </div>
        ) : (
          <>
            <span>Click or drag &amp; drop your resume</span>
            <span className="drop-sub">PDF supported</span>
          </>
        )}
      </div>

      <button className="btn-analyze" onClick={handleUpload} disabled={loading}>
        {loading ? (
          <span className="btn-loading">
            <span className="btn-spinner" />
            Analyzing…
          </span>
        ) : "Analyze Resume \u2192"}
      </button>

      {error && <p className="error-msg">{"\u26A0\uFE0F"} {error}</p>}

      <div className="tip-card">
        <p className="tip-text">
          <strong>Tip: </strong>
          Make sure your resume clearly lists your skills and technologies for the most accurate match.
        </p>
      </div>
    </div>
  );
}

/* ================= MAIN APP ================= */
export default function App() {
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);

  return (
    <div className="app">
      <UploadPanel onResult={setResult} onLoading={setLoading} loading={loading} />
      <ResultPanel result={result} loading={loading} />
    </div>
  );
}
